import React, { useState } from "react";
import { Panel } from "@/components/Panel";
import {
  ChevronDown, ChevronUp, Wifi, WifiOff, Power, AlertTriangle,
} from "lucide-react";

/**
 * <ConnectorCard />
 *
 * Mobile-first connector tile used by Xero / MYOB / Square panels on
 * /app/sources. Replaces dense always-expanded descriptions with a
 * collapsed header (logo · name · status · primary CTA) and a chevron
 * to reveal supporting copy + secondary actions.
 *
 * Touch target ≥ 44px, single-column on mobile, two-column status row
 * on md+.
 *
 * Props:
 *  - title:       string  // e.g. "// SQUARE POS · LIVE TRANSACTION FEED"
 *  - testId:      string  // root data-testid (e.g. "square-connection-panel")
 *  - icon:        Component (lucide icon — rendered in the brand pill)
 *  - name:        string  // "Connect Square POS" / business name when connected
 *  - tagline:     string  // one-line description (mobile-friendly)
 *  - status:      "connected"|"disconnected"|"warning"|"none"
 *  - statusLabel: string  // "● LIVE" / "DISCONNECTED" / "PICK CF" / ""
 *  - meta:        node    // extra inline metadata (location · currency · "Connected 5 min ago")
 *  - badges:      node    // small chips (SANDBOX / AU / etc.)
 *  - primaryAction: node  // the main CTA button (Connect / Pick CF / Sync)
 *  - secondaryActions: node // (Disconnect / Resync). Hidden behind chevron on mobile.
 *  - extra: node          // freeform extra content shown only when expanded
 */
export default function ConnectorCard({
  title,
  testId,
  icon: Icon,
  name,
  tagline,
  status = "none",
  statusLabel = "",
  meta = null,
  badges = null,
  primaryAction = null,
  secondaryActions = null,
  extra = null,
}) {
  const [open, setOpen] = useState(false);

  const accent =
    status === "connected" ? "#00E676" :
    status === "warning" ? "#FFB000" :
    status === "disconnected" ? "#FF6B47" : "#FFB000";

  const StatusIcon =
    status === "connected" ? Wifi :
    status === "warning" ? AlertTriangle :
    status === "disconnected" ? WifiOff :
    Power;

  return (
    <Panel title={title} testId={testId}>
      <div className="p-4 sm:p-5">
        {/* HEADER ROW — always visible */}
        <div className="flex items-start gap-3">
          <span
            className="border p-3 flex-shrink-0"
            style={{
              borderColor: accent + "55",
              backgroundColor: accent + "11",
            }}
          >
            {Icon ? (
              <Icon className="w-6 h-6" style={{ color: accent }} />
            ) : (
              <StatusIcon className="w-6 h-6" style={{ color: accent }} />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-base sm:text-lg leading-tight truncate">
                  {name}
                </h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {statusLabel && (
                    <span
                      data-testid={`${testId}-status`}
                      className="px-2 py-0.5 border font-mono text-[9px] uppercase tracking-widest"
                      style={{
                        borderColor: accent + "66",
                        color: accent,
                        backgroundColor: accent + "0d",
                      }}
                    >
                      {statusLabel}
                    </span>
                  )}
                  {badges}
                </div>
              </div>
            </div>
            {/* Tagline — clamped on mobile, full on desktop */}
            {tagline && (
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed line-clamp-2 sm:line-clamp-none">
                {tagline}
              </p>
            )}
          </div>
        </div>

        {/* PRIMARY CTA — full width on mobile, inline on desktop */}
        {primaryAction && (
          <div className="mt-4">{primaryAction}</div>
        )}

        {/* META row */}
        {meta && (
          <div className="mt-3 font-mono text-[10px] text-zinc-500">{meta}</div>
        )}

        {/* DETAILS toggle — only on mobile if secondary actions / extra exist */}
        {(secondaryActions || extra) && (
          <>
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              data-testid={`${testId}-toggle`}
              className="sm:hidden mt-4 w-full border-t border-zinc-900 pt-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white py-2 transition-colors"
            >
              {open ? (
                <>
                  <ChevronUp className="w-3 h-3" /> LESS DETAIL
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" /> MORE OPTIONS
                </>
              )}
            </button>
            <div
              className={`${open ? "block" : "hidden"} sm:block mt-3 sm:mt-4 space-y-3`}
              data-testid={`${testId}-details`}
            >
              {secondaryActions && (
                <div className="flex flex-wrap gap-2">{secondaryActions}</div>
              )}
              {extra}
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}
